import * as React from 'react';
import {Button, Col, Form, message, Row} from 'antd';
import {FormComponentProps} from 'antd/lib/form';

import {
  AmountAndTotal,
  ErrorResponse,
  GetAmountAndTotalForOverrideRequest,
  GetTotalForOverrideRequest,
  Total
} from '../api/dtos/dtos';
import locales from '../locales';

import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorAlert} from '../common/ErrorAlert';
import {SelectValue} from 'antd/lib/select';
import {
  getAmountTypeLabel,
  getAmountTypeOptions,
  getAmountUnitForAmountType,
  getPriceForItem,
  isSameAmountTypeCategory
} from '../common/AmountType';
import SelectFormItem from '../common/form-items/SelectFormItem';
import NumberFormItem from '../common/form-items/NumberFormItem';
import {groupNumber, numberGroupFormatter, numberGroupParser, renderPriceColumn} from '../utils/number-utils';
import SectionHeaderFormItem from '../common/form-items/SectionHeaderFormItem';
import StaticFormItem from '../common/form-items/StaticFormItem';
import './ClientRentalSummaryOverrideForm.css';
import furoApi from '../api/FuroApi';
import ItemDetailsAlert from '../item-details/ItemDetailsAlert';
import FormItem from 'antd/lib/form/FormItem';
import Config from '../config';
import {debounce} from 'lodash';
import * as formUtils from '../utils/form-utils';
import {
  ClientRentalSummary,
  DeleteOverrideRequest,
  RentalItemOverride,
  SaveOverrideRequest
} from "../api/dtos/summaries";

interface ClientRentalSummaryOverrideFormProps extends FormComponentProps {
  summary: ClientRentalSummary;
  selectedYear: number;
  selectedMonth: number;
  onFormSubmit: () => void;
  onFormCancel: () => void;
}

const FIELD_AMOUNT_TYPE = 'amountType';
const FIELD_PRICE = 'price';
const FIELD_AMOUNT = 'amount';
const FIELD_TOTAL = 'total';


interface ClientRentalSummaryOverrideFormState {
  error?: ErrorResponse;
}

interface Values {
  price: number;
  amountType: string;
  amount: number | null;
  total: number | null;
}

class ClientRentalSummaryOverrideForm extends React.Component<ClientRentalSummaryOverrideFormProps, ClientRentalSummaryOverrideFormState> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly state: ClientRentalSummaryOverrideFormState = {};

  private readonly debouncedGetAmountAndTotalForOverride: (newAmountType: string, newPrice?: number) => void =
    debounce(this.getAmountAndTotalForOverride, Config.SEARCH_DEBOUNCE_TIMEOUT).bind(this);

  private readonly debouncedGetTotalForOverride: (args: { price?: number, amount: number | undefined }) => void =
    debounce(this.getTotalForOverride, Config.SEARCH_DEBOUNCE_TIMEOUT).bind(this);

  public render() {
    const {form, summary, summary: {rentalItem}} = this.props;
    const values = this.getValuesFromSummaryOrRentalItem();

    const originalPrice = renderPriceColumn(rentalItem.price);
    const originalAmountType = getAmountTypeLabel(rentalItem.amountType);
    const originalAmount = `${groupNumber(summary.amount)} ${getAmountUnitForAmountType(rentalItem.amountType)}`;
    const originalTotal = renderPriceColumn(summary.total);

    const itemFormItemLayout = {
      labelCol: {
        xs: {span: 24},
        sm: {span: 4},
      },
      wrapperCol: {
        xs: {span: 24},
        sm: {span: 20},
      },
    };

    // values.amountType is for the first render - getFieldsValue returns undefined
    const formAmountType = form.getFieldValue(FIELD_AMOUNT_TYPE) || values.amountType;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Form>

          <FormItem {...itemFormItemLayout} label={locales.item}>
            <ItemDetailsAlert item={rentalItem.item}/>
          </FormItem>

          <Row>
            <Col span={12}>
              <SectionHeaderFormItem title={locales.on_rental_form}/>
              <StaticFormItem label={locales.settlement} initialValue={originalAmountType}/>
              <StaticFormItem label={locales.net_price} initialValue={originalPrice}/>
              <StaticFormItem label={locales.amount} initialValue={originalAmount}/>
              <StaticFormItem label={locales.total} initialValue={originalTotal}/>
            </Col>

            <Col span={12}>
              <SectionHeaderFormItem title={locales.overridden}/>
              <SelectFormItem
                form={form}
                fieldId={FIELD_AMOUNT_TYPE}
                label={locales.settlement}
                initialValue={values.amountType}
                options={getAmountTypeOptions(rentalItem.item.itemType)}
                onSelect={this.onAmountTypeSelect}
                required={true}
              />

              <NumberFormItem
                label={locales.net_price}
                fieldId={FIELD_PRICE}
                form={form}
                initialValue={values.price}
                required={true}
                min={0}
                precision={0}
                formatter={numberGroupFormatter}
                onChange={this.onPriceChange}
              >
                <> {locales.app_currency}</>
              </NumberFormItem>

              <NumberFormItem
                label={locales.amount}
                fieldId={FIELD_AMOUNT}
                form={form}
                initialValue={values.amount}
                required={true}
                min={0}
                step={0.01}
                precision={2}
                formatter={numberGroupFormatter}
                parser={numberGroupParser}
                hideIntegerDecimals={true}
                onChange={this.onAmountChange}
              >
                <> {getAmountUnitForAmountType(formAmountType)}</>
              </NumberFormItem>

              <NumberFormItem
                label={locales.total}
                fieldId={FIELD_TOTAL}
                form={form}
                disabled={true}
                initialValue={values.total}
                required={false}
                formatter={numberGroupFormatter}
              >
                <span style={{fontWeight: 'bold'}}>&nbsp;{locales.app_currency}</span>
              </NumberFormItem>
            </Col>
          </Row>

          {this.props.children}
          {this.state.error && <ErrorAlert style={{margin: '16px 0'}} error={this.state.error}/>}

          <FormItem {...formUtils.tailFormItemLayout}>
            <Button type="primary" onClick={this.onFormSubmit}>{locales.save}</Button>
            <Button style={{marginLeft: 8}} onClick={this.props.onFormCancel}>{locales.cancel}</Button>
            <Button type="danger" icon="delete" style={{marginLeft: 8}}
                    onClick={this.deleteOverride}>{locales.override_delete}</Button>
          </FormItem>

        </Form>
      </>
    );
  }

  private getAmountFieldValue = (): number | undefined => this.props.form.getFieldValue(FIELD_AMOUNT);
  private getPriceFieldValue = (): number | undefined => this.props.form.getFieldValue(FIELD_PRICE);
  private getAmountTypeFieldValue = (): string => this.props.form.getFieldValue(FIELD_AMOUNT_TYPE);

  /**
   * If a summary is defined (containing the overrides) display values from that,
   * else display the original values from rentalItem.
   */
  private getValuesFromSummaryOrRentalItem = (): Values => {
    const {summary: {rentalItem, override, amount, total}} = this.props;
    return override ? override : {
      price: rentalItem.price,
      amountType: rentalItem.amountType,
      amount,
      total,
    };
  };

  /**
   * Getting the price:
   * - if override is present and amount type's category is the same as the selected one return override's price
   *    - if the category has changed e.g daily -> pcs there's no point of retaining the price value
   * - if not return the price that was recorded in the rentalItem during rental or returnal - if the amount type's category is the same
   * - if not get the price configured for the item
   */
  private getPriceForAmountType = (amountType: string): number | undefined => {
    const {summary: {override, rentalItem, rentalItem: {item}}} = this.props;
    if (override && isSameAmountTypeCategory(amountType, override.amountType)) {
      return override.price;

    } else if (isSameAmountTypeCategory(amountType, rentalItem.amountType)) {
      return rentalItem.price;

    } else {
      return getPriceForItem(item, amountType);
    }
  };

  private onAmountTypeSelect = (value: SelectValue) => {
    const {form} = this.props;
    const newAmountType = String(value);

    // Update price field
    const newPrice = this.getPriceForAmountType(newAmountType);
    form.setFieldsValue({[FIELD_PRICE]: newPrice});

    this.debouncedGetAmountAndTotalForOverride(newAmountType, newPrice);
  };

  private onPriceChange = (value: number | undefined) => {
    this.debouncedGetTotalForOverride({
      price: value,
      amount: this.getAmountFieldValue(),
    });
  };

  private onAmountChange = (value: number | undefined) => {
    this.debouncedGetTotalForOverride({
      price: this.getPriceFieldValue(),
      amount: value,
    });
  };

  private getAmountAndTotalForOverride(newAmountType: string, newPrice?: number) {
    const {form, summary: {rentalItem}, selectedYear, selectedMonth} = this.props;

    const request: GetAmountAndTotalForOverrideRequest = {
      rentalItemId: rentalItem.id!!,
      year: selectedYear,
      month: selectedMonth,
      priceOverride: newPrice ? newPrice : 0,
      amountTypeOverride: newAmountType,
    };

    const onSuccess = (response: AmountAndTotal) => {
      this.setState({error: undefined});
      form.setFieldsValue({
        [FIELD_AMOUNT]: response.amount,
        [FIELD_TOTAL]: response.total,
      });
    };

    furoApi.summaries.getAmountAndTotalForOverride(request)
      .then(onSuccess)
      .catch(this.onApiError);
  };

  private getTotalForOverride(args: { price?: number, amount: number | undefined }) {
    const {summary: {rentalItem}, selectedYear, selectedMonth, form} = this.props;

    const request: GetTotalForOverrideRequest = {
      rentalItemId: rentalItem.id!!,
      year: selectedYear,
      month: selectedMonth,
      priceOverride: args.price ? args.price : 0,
      amountTypeOverride: this.getAmountTypeFieldValue(),
      amountOverride: args.amount ? args.amount : 0,
    };

    const onSuccess = (response: Total) => {
      this.setState({error: undefined});
      form.setFieldsValue({
        [FIELD_TOTAL]: response.total,
      });
    };

    furoApi.summaries.getTotalForOverride(request)
      .then(onSuccess)
      .catch(this.onApiError);
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };

  private onFormSubmit = () => {
    const {form} = this.props;
    form.validateFieldsAndScroll((err) => {
      if (err) {
        return;
      }

      this.saveOverride({
        price: this.getPriceFieldValue()!,
        amountType: this.getAmountTypeFieldValue(),
        amount: this.getAmountFieldValue()!,
        total: null,  // Backend will recalculate it
      });
    });
  };

  private saveOverride = (override: RentalItemOverride) => {
    const {selectedMonth, selectedYear, summary: {rentalItem}} = this.props;

    const request: SaveOverrideRequest = {
      rentalItemId: rentalItem.id!,
      year: selectedYear,
      month: selectedMonth,
      override,
    };

    const promise = furoApi.summaries.saveOverride(request)
      .then(this.onSaveSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onSaveSuccess = () => {
    message.success(locales.save_success);
    this.setState({error: undefined}, this.props.onFormSubmit);
  };

  private deleteOverride = () => {
    const {selectedMonth, selectedYear, summary: {rentalItem}} = this.props;

    const request: DeleteOverrideRequest = {
      rentalItemId: rentalItem.id!,
      year: selectedYear,
      month: selectedMonth,
    };

    const promise = furoApi.summaries.deleteOverride(request)
      .then(this.onSaveSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

}

export default Form.create<ClientRentalSummaryOverrideFormProps>()(ClientRentalSummaryOverrideForm);



// WEBPACK FOOTER //
// ./src/summary/ClientRentalSummaryOverrideForm.tsx